import { lazy, useEffect } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import BlankPage from 'common/components/BlankPage';
import Utils from 'common/utils';
import JSRuntime from 'common/utils/JSRuntime';
import { convert, useStore } from 'idpBase/store';
import withClientLogin from './hoc/withClientLogin';
import withLogin from './hoc/withLogin';

const Login = lazy(() => import('../login'));
const SSOLogin = lazy(() => import('../sso-login/RouteEntry'));
const ResetPassword = lazy(() => import('../reset-password'));
const Invitation = lazy(() => import('../invitation'));
const CidpRouteEntry = lazy(() => import('../cidp/RouteEntry'));
const BindMobile = lazy(() => import('../bind-mobile'));

// 依赖登录态的的页面
const Certify = withLogin(lazy(() => import('../certify')));
const Profile = withLogin(lazy(() => import('../profile/RouteEntry')));
const Auth = withClientLogin(withLogin(lazy(() => import('../auth/RouteEntry'))));
const AuthEnterprise = withClientLogin(
  withLogin(lazy(() => import('../auth-enterprise/RouteEntry'))),
);
const SelectAccount = withLogin(lazy(() => import('../select-account')));
const Consent = withLogin(lazy(() => import('../consent')));
const AppAuthorization = withLogin(lazy(() => import('../app-authorization/RouteEntry')));

const IndexElement = convert(() => {
  const { rootStore } = useStore();
  const { userInfo } = rootStore;
  const { appType } = JSRuntime.parseLocationSearch(undefined, {
    transferNum: false,
  });

  // 有登录态 && 不是自定义域名
  if (!_.isEmpty(mobx.toJS(userInfo.accountUser)) && !JSRuntime.isCustomDomain) {
    if (appType) return <Navigate to="/app_authorization" />;

    rootStore.redirectToLandingPage();
    return null;
  }

  return <Navigate to={JSRuntime.isCustomDomain ? '/sso_login/identity' : '/login'} />;
});

const RouteEntry = () => {
  const isMustSsoLoginEnv = JSRuntime.isCustomDomain;

  useEffect(() => {
    const hash = Utils.getPureHashPath();
    if (hash === '#/app_authorization/authorize') return;
    const meta = document.createElement('meta');
    meta.httpEquiv = 'Content-Security-Policy';
    meta.content = "form-action 'none'";
    document.head.appendChild(meta);
  }, []);

  return useRoutes([
    {
      index: true,
      element: <IndexElement />,
    },
    // 自然人登录页
    {
      path: 'login/*',
      element: isMustSsoLoginEnv ? <Navigate to="/sso_login/identity" /> : <Login />,
    },
    // sso登录页
    { path: 'sso_login/*', element: <SSOLogin /> },
    // sso登录成功、失败之后的中转页，后端跳转过来的
    { path: 'cidp/*', element: <CidpRouteEntry /> },
    // 注册页
    {
      path: 'register',
      element: JSRuntime.isIntl ? <Navigate to="/" /> : <Login currentPage={4} />,
    },
    // 第三方授权页
    { path: 'app_authorization/*', element: <AppAuthorization /> },
    // 个人中心
    { path: 'profile/*', element: <Profile /> },
    // 激活邀请 - 发送短信中携带的地址
    { path: 'invitation', element: <Invitation /> },
    // 重置密码 - 发送短信中携带的地址
    // eslint-disable-next-line regex/invalid
    !JSRuntime.isPrivate ? { path: 'resetPwd/:token', element: <ResetPassword /> } : {},
    // 选择可以继续登录的账号
    { path: 'select_account', element: <SelectAccount /> },
    // 当前登录账号+选择其他账号
    { path: 'consent', element: <Consent /> },
    // 个人认证
    { path: 'auth/*', element: JSRuntime.isIntl ? <Navigate to="/" /> : <Auth /> },
    // 企业认证
    {
      path: 'auth-enterprise/*',
      element: JSRuntime.isIntl ? <Navigate to="/" /> : <AuthEnterprise />,
    },
    // 二次验证
    { path: 'certify', element: <Certify /> },
    { path: 'bind-mobile', element: <BindMobile /> },
    // eslint-disable-next-line regex/invalid
    { path: 'notFound', element: <BlankPage type="NOT_FOUND" /> },
    { path: '*', element: <Navigate to="/notFound" /> },
  ]);
};

export default RouteEntry;
